import React, { forwardRef } from "react";
import NextLink from "next/link";
import { observer } from "mobx-react";
import Button from "ui/Button/Button";
import ProductPreviewImage from "ui/ProductPreviewImage/ProductPreviewImage";
import AddToFavorites from "common/AddToFavorites/AddToFavorites";
import AddToCartSingle from "common/AddToCartSingle/AddToCartSingle";
import Price from "common/Price/Price";
import Badges from "common/Badges/Badges";
import { useStore } from "store";
import { useUrl } from "helpers/hooks";
import { cn } from "helpers/style";
import { Props as PreviewProps, CommonProps } from "../../ProductPreview";
import styles from "./HorizontalSmall.module.scss";

type Props = PreviewProps & CommonProps;

const HorizontalSmall = forwardRef<HTMLDivElement, Props>(
  (
    {
      product,
      imageSrc,
      analyticsMeta,
      onClick,
      onOpenNotify,
      hideCartToast,
      onInteract,
      disableLazyImage,
      pricePrefix,
      price,
      isEmployeeMode,
    },
    ref
  ) => {
    const isAvailable =
      (product.availableForOrder || product.availableForBooking) &&
      Boolean(price);
    const { getEntityLinkProps } = useUrl();
    const { user } = useStore();

    return (
      <div
        ref={ref}
        data-id={product.id}
        data-diginetica="produc_special_diginetica"
        className={cn([
          styles.horizontalSmallWrapper,
          product.isAdv && product.warning && styles.horizontalSmallWrapperAdv,
        ])}
      >
        <div className={styles.horizontalSmall}>
          <div className={styles.horizontalSmallUpperActions}>
            <AddToFavorites product={product} />
          </div>

          <div
            className={cn([
              styles.horizontalSmallImageContainer,
              product.isAdv &&
                product.warning &&
                styles.horizontalSmallImageContainerSmall,
            ])}
          >
            <ProductPreviewImage
              onClick={() => onClick && onClick({ product })}
              onMouseDown={onInteract}
              linkProps={getEntityLinkProps("product", product)}
              ariaLabel={product.name}
              src={imageSrc}
              type="horizontalSmall"
              priority={disableLazyImage}
              title={product.name}
              alt={product.name}
            />

            {product.badges && product.badges.length > 0 && (
              <div
                className={cn([
                  styles.horizontalSmallBadges,
                  styles.horizontalSmallBadgesFullWidth,
                ])}
              >
                <Badges
                  badges={product.badges}
                  tooltipProps={{
                    maxWidth: 292,
                    placement: "top-start",
                  }}
                  alt="Шильдик акции"
                />
              </div>
            )}
          </div>

          <div className={styles.horizontalSmallContent}>
            <NextLink
              {...getEntityLinkProps("product", product)}
              className={cn([
                styles.horizontalSmallContentTitle,
                user.isAuth && styles.horizontalSmallContentTitleShifted,
                product.isAdv &&
                  product.warning &&
                  styles.horizontalSmallContentTitleAdv,
              ])}
              passHref
            >
              <span
                onClick={() => onClick && onClick({ product })}
                onMouseDown={onInteract}
              >
                {product.name}
              </span>
            </NextLink>

            <div className={styles.horizontalSmallPriceContainer}>
              {!isAvailable ? (
                <div
                  className={styles.horizontalSmallPriceContainerNotAvailable}
                >
                  <Button
                    onClick={onOpenNotify}
                    className={
                      styles.horizontalSmallPriceContainerNotAvailableButton
                    }
                    orange
                    outlined
                    fluid
                    small
                  >
                    Узнать о поступлении
                  </Button>
                </div>
              ) : (
                <>
                  <Price
                    hasOnlyBookingPrices={
                      product.hasOnlyBookingPrices || isEmployeeMode
                    }
                    hasPrefix={price.hasPrefix}
                    price={price.price}
                    priceOld={price.priceOld}
                    discount={price.discount}
                    prefix={pricePrefix}
                  />

                  <AddToCartSingle
                    product={product}
                    price={price}
                    analyticsMeta={analyticsMeta}
                    hideCartToast={hideCartToast}
                  />
                </>
              )}
            </div>
          </div>
        </div>
        {product.isAdv && product.warning && (
          <div className={styles.horizontalSmallDisclaimer}>
            {product.warning}
          </div>
        )}
      </div>
    );
  }
);

export default observer(HorizontalSmall);
